import { createResource, For, Show } from 'solid-js';
import { A } from '@solidjs/router';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { useTranslation } from '../hooks/useTranslation';
import { useApi, isOk } from '../api';
import ItemStackView from '../components/ItemStackView';
import { fromIdentifier } from '../services/itemRefAdapters';
import { LOCATION_KIND_LABELS, parseLocation } from '../services/gameLocation';
import styles from './DropConfigs.module.css';
import { Citybuild } from '../models/Citybuild';
import { formatDate } from '../services/dateFormat';

interface DropConfig {
    id: string;
    name: string;
    description: string;
    location: string;
    citybuild: Citybuild;
    itemsToDrop: string[];
    everythingExceptRepairable: boolean;
    createdAt: string;
    updatedAt: string;
    versionNumber: number;
}

export default function DropConfigs() {
    const api = useApi();
    const { t } = useTranslation();

    const fetchDropConfigs = async (): Promise<DropConfig[]> => {
        const response = await api.minecraftConfig.getAllDropConfigs();
        if (!isOk(response.data)) {
            throw new Error('Failed to fetch drop configs');
        }
        return ((response.data.value as unknown as DropConfig[]) ?? []);
    };

    const [dropConfigs] = createResource(fetchDropConfigs);

    const getCitybuildLabel = (citybuild: Citybuild): string => {
        switch (citybuild) {
            case Citybuild.CB1: return 'CB1';
            case Citybuild.CB2: return 'CB2';
            case Citybuild.CB3: return 'CB3';
            case Citybuild.CB4: return 'CB4';
            case Citybuild.CB5: return 'CB5';
            case Citybuild.CB6: return 'CB6';
            case Citybuild.CB7: return 'CB7';
            case Citybuild.CB8: return 'CB8';
            case Citybuild.CB9: return 'CB9';
            case Citybuild.CB10: return 'CB10';
            case Citybuild.CB11: return 'CB11';
            case Citybuild.CB12: return 'CB12';
            case Citybuild.CB13: return 'CB13';
            case Citybuild.CB14: return 'CB14';
            case Citybuild.CB15: return 'CB15';
            case Citybuild.CB16: return 'CB16';
            case Citybuild.CB17: return 'CB17';
            case Citybuild.CB18: return 'CB18';
            case Citybuild.CB19: return 'CB19';
            case Citybuild.CB20: return 'CB20';
            case Citybuild.CB21: return 'CB21';
            case Citybuild.CB22: return 'CB22';
            case Citybuild.Nature: return 'NATURE';
            case Citybuild.Extreme: return 'EXTREME';
            default: return 'UNKNOWN';
        }
    };
    
    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <div class={styles.titleSection}>
                    <h1><Text val="DROP_CONFIGS" /></h1>
                    <p class={styles.subtitle}>
                        <Text val="MANAGE_YOUR_MINECRAFT_DROP_CONFIGURATIONS" />
                    </p>
                </div>
                <A href="/dropconfigs/new" class={styles.createButton}>
                    <i class="fa-solid fa-plus"></i>
                    <Text val="CREATE_DROP_CONFIG" />
                </A>
            </div>

            <Show when={!dropConfigs.loading} fallback={
                <div class={styles.loading}>
                    <i class="fa-solid fa-spinner fa-spin"></i>
                    <Text val="LOADING_DROP_CONFIGS" />
                </div>
            }>
                <Show when={dropConfigs.error} fallback={
                    <Show when={(dropConfigs() || []).length > 0} fallback={
                        <EmptyState
                            icon="fa-box-open"
                            title={t('NO_DROP_CONFIGS_FOUND')}
                            hint={t('CREATE_YOUR_FIRST_DROP_CONFIG')}
                            action={
                                <A href="/dropconfigs/new" class={styles.createFirstButton}>
                                    <i class="fa-solid fa-plus"></i>
                                    <Text val="CREATE_DROP_CONFIG" />
                                </A>
                            }
                        />
                    }>
                        <div class={styles.grid}>
                            <For each={dropConfigs()}>
                                {(dropConfig) => (
                                    <div class={styles.dropConfigCard}>
                                        <div class={styles.cardHeader}>
                                            <div class={styles.cardTitle}>
                                                <h3>{dropConfig.name}</h3>
                                                <div class={styles.badges}>
                                                    <Show when={parseLocation(dropConfig.location)}>
                                                        {(location) => (
                                                            <span class={styles.badge}>
                                                                <Text val={LOCATION_KIND_LABELS[location().kind]} />
                                                            </span>
                                                        )}
                                                    </Show>
                                                    <span class={`${styles.badge} ${styles.citybuild}`}>
                                                        {getCitybuildLabel(dropConfig.citybuild)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class={styles.cardActions}>
                                                <A href={`/dropconfigs/${dropConfig.id}`} class={styles.actionButton}>
                                                    <i class="fa-solid fa-eye"></i>
                                                </A>
                                            </div>
                                        </div>

                                        <div class={styles.cardContent}>
                                            <p class={styles.description}>{dropConfig.description}</p>
                                            
                                            <div class={styles.cardInfo}>
                                                <div class={styles.infoItem}>
                                                    <i class="fa-solid fa-map-marker-alt"></i>
                                                    <span>{dropConfig.location}</span>
                                                </div>
                                                <div class={styles.infoItem}>
                                                    <i class="fa-solid fa-cube"></i>
                                                    <span>
                                                        {dropConfig.itemsToDrop.length} <Text val="ITEMS" />
                                                    </span>
                                                </div>
                                            </div>

                                            <div class={styles.itemsPreview}>
                                                <For each={dropConfig.itemsToDrop.slice(0, 6)}>
                                                    {(itemId) => (
                                                        <ItemStackView stack={fromIdentifier(itemId)} titleOverride={itemId} />
                                                    )}
                                                </For>
                                                <Show when={dropConfig.itemsToDrop.length > 6}>
                                                    <div class={styles.moreItems}>
                                                        +{dropConfig.itemsToDrop.length - 6}
                                                    </div>
                                                </Show>
                                            </div>
                                        </div>

                                        <div class={styles.cardFooter}>
                                            <span class={styles.timestamp}>
                                                <Text val="UPDATED" />: {formatDate(dropConfig.updatedAt)}
                                            </span>
                                            <A href={`/dropconfigs/${dropConfig.id}`} class={styles.viewButton}>
                                                <Text val="VIEW_DETAILS" />
                                                <i class="fa-solid fa-arrow-right"></i>
                                            </A>
                                        </div>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                }>
                    <div class={styles.errorState}>
                        <i class="fa-solid fa-exclamation-triangle"></i>
                        <h3><Text val="ERROR_LOADING_DROP_CONFIGS" /></h3>
                        <p>{dropConfigs.error?.message}</p>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
